import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Identifiable,
  Project,
  ProjectConfiguration,
  ProjectPortfolio,
  zone as z,
} from "@planzone/dto";
import {
  useRetrieveCurrentPlanzone,
  useRetrieveProjectDetailsConfiguration,
  useRetrieveProjectToolsConfiguration,
  useRetrieveSecondaryCriteriaTemplate,
  useUseProjectModel,
} from "@planzone/view-models";
import { useRoles } from "@scenes/safeguard";
import { Modal } from "@common/modal";
import { Button } from "@common/button";
import { ProjectCreationForm } from "../../projectCreationForm";
import { Props } from "./types";
import styles from "./styles.scss";

function useRequiredFields(project: Project) {
  const { planzone } = useRetrieveCurrentPlanzone();
  const { template: planzoneTemplate } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "project",
  );
  const { template: projectTemplate } = useRetrieveSecondaryCriteriaTemplate(
    project.uuid,
    "project",
  );
  const template = [...planzoneTemplate, ...projectTemplate];
  const { configuration } = useRetrieveProjectDetailsConfiguration(planzone);
  const roles = useRoles({ project });
  const profile =
    configuration.find((p) =>
      p.roles.find((role) =>
        roles.find((r) => r.uuid === role || r.name === role),
      ),
    ) || configuration.length > 0
      ? configuration[0]
      : {
          name: "",
          roles: [],
          elements: [],
        };
  const fields = template.filter(
    ({ uuid, attr }) =>
      !!profile.elements.find(({ key }) => key === uuid) &&
      attr.required === "true",
  );

  return !!fields.find(
    ({ uuid }) =>
      !project.attributes[uuid] || project.attributes[uuid].length === 0,
  );
}

function ProjectEdition({
  zone,
  projectModel,
  initialPortfolios,
  isMultiProject,
  selectZone,
  close,
}: Props) {
  const { t } = useTranslation();
  const { isRequestPending, useProjectModel } = useUseProjectModel(
    zone || { ...z, uuid: "" },
    projectModel,
  );
  const {
    isRequestPending: isConfRequestPending,
    isRequestSuccess: isConfRequestSuccess,
    isRequestFailure: isConfRequestFailure,
    config,
    retrieveProjectToolsConfiguration,
  } = useRetrieveProjectToolsConfiguration(projectModel);
  const projectDuration = projectModel.rootList.endDate
    ? new Date(projectModel.rootList.endDate).getTime() -
      new Date(projectModel.rootList.startDate).getTime()
    : 0;
  const today = new Date();
  const todayStart = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  ).toISOString();
  const [formData, setFormData] = React.useState<
    Project & { portfolios: Identifiable<ProjectPortfolio>[] }
  >({
    ...projectModel,
    isTemplate: false,
    portfolios: [],
    rootList: {
      ...projectModel.rootList,
      startDate: todayStart,
      endDate: projectDuration
        ? new Date(
            new Date(todayStart).getTime() + projectDuration,
          ).toISOString()
        : projectModel.rootList.endDate,
    },
  });

  const [configuration, setConfiguration] =
    useState<ProjectConfiguration>(config);
  const hasEmptyRequiredFields = useRequiredFields(formData);

  React.useEffect(() => {
    !isConfRequestPending &&
      !isConfRequestSuccess &&
      !isConfRequestFailure &&
      retrieveProjectToolsConfiguration(projectModel);
  }, []);

  React.useEffect(() => {
    setConfiguration(config);
  }, [config]);

  React.useEffect(() => {
    formData.rootList.startDate &&
      formData.rootList.endDate &&
      setFormData({
        ...formData,
        rootList: {
          ...formData.rootList,
          endDate: new Date(
            new Date(formData.rootList.startDate).getTime() + projectDuration,
          ).toISOString(),
        },
      });
  }, [formData.rootList.startDate]);

  React.useEffect(() => {
    setFormData((formData) => ({ ...formData, portfolios: initialPortfolios }));
  }, [initialPortfolios.length]);

  return (
    <Modal header={t("projectCreationFromProjectModel.title")} close={close}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          zone &&
            useProjectModel({
              zone,
              projectModel,
              project: formData,
              configuration,
            });
        }}
        className={styles.form}
      >
        <ProjectCreationForm
          zone={zone}
          formData={formData}
          configuration={configuration}
          editForm={setFormData}
          setConfiguration={setConfiguration}
          isMultiProject={isMultiProject}
          template={projectModel}
          selectZone={selectZone}
        />
        <div className={styles.buttons}>
          <Button type="cancel" press={close}>
            {t("projectCreationFromProjectModel.cancel")}
          </Button>
          <Button
            type="submit"
            status={isRequestPending ? "pending" : "none"}
            isDisabled={!zone || hasEmptyRequiredFields}
          >
            {t("projectCreationFromProjectModel.submit")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export { ProjectEdition };
